// Languages. The menu, the companions' descriptions, the names of their actions, skins and
// outfits, and what they say in speech bubbles can be in any of these. English is built in: it's
// what the code and the designs say. Every other language is a table of texts by key, which the
// host loads and hands to setLanguage; a text the table lacks stays English.
//
//   tr('menu.size', 'Size')                                   → 'Größe' in German
//   tr('menu.onScreenCount', '{n} of {m} on screen', { n: 3, m: 8 })
//   tr('menu.companions', { one: '{n} companion', other: '{n} companions' }, { n: 85 })
//
// Counted texts are objects of plural forms (Intl.PluralRules: one, few, many, other ...), in the
// table too, so every language can have the forms it needs (Russian has three); vars.n picks one.

// Each language's code and its name in itself, in the order the menu lists them.
export const LANGUAGES = {
  en: 'English',
  de: 'Deutsch',
  es: 'Español',
  fr: 'Français',
  'pt-BR': 'Português (Brasil)',
  it: 'Italiano',
  ja: '日本語',
  'zh-Hans': '简体中文',
  ko: '한국어',
  ru: 'Русский',
};

// The languages companions speak in their speech bubbles: the bubbles' pixel font writes their
// letters (Latin and Cyrillic capitals, engine/pixelfont.js). In the others (Japanese, Chinese,
// Korean) a bubble stays as written, in English.
export const SPOKEN = new Set(['en', 'de', 'es', 'fr', 'pt-BR', 'it', 'ru']);

let current = 'en', table = {}, plurals = new Intl.PluralRules('en');

export const language = () => current;

// The language now and its texts ({ key: text }). An unknown code is English.
export function setLanguage(code, texts = {}) {
  current = Object.hasOwn(LANGUAGES, code) ? code : 'en';
  table = current === 'en' ? {} : texts;
  plurals = new Intl.PluralRules(current);
}

// More texts for the language in use, when they come in a part of their own (a website loads the
// menu's texts with the menu). Texts for a language no longer in use are dropped.
export function addTexts(code, texts = {}) {
  if (code === current && current !== 'en') table = { ...table, ...texts };
}

// The supported language closest to a list of preferred ones, best first (navigator.languages,
// the system's languages, a page's lang): 'de-AT' → 'de', 'pt' → 'pt-BR', 'zh-CN' → 'zh-Hans'.
// Traditional Chinese has no table yet, so it moves on to the next preference. English when
// nothing fits.
export function matchLanguage(tags = []) {
  for (const tag of tags) {
    const [base, ...rest] = String(tag ?? '').trim().toLowerCase().split(/[-_]/);
    if (!base) continue;
    if (base === 'zh') {
      if (rest.some(part => ['hant', 'tw', 'hk', 'mo'].includes(part))) continue;
      return 'zh-Hans';
    }
    if (base === 'pt') return 'pt-BR';
    const code = Object.keys(LANGUAGES).find(c => c.toLowerCase() === base);
    if (code) return code;
  }
  return 'en';
}

// A text in the current language: the table's text under key, else the English. A counted text
// picks its form by vars.n. {name} in it is filled in from vars (one vars lacks stays as it is).
export function tr(key, english, vars) {
  let text = Object.hasOwn(table, key) ? table[key] : english;
  if (text && typeof text === 'object') {
    const n = Number(vars?.n ?? 0);
    text = text[plurals.select(n)] ?? text.other ?? english?.other ?? '';
  }
  return fill(String(text ?? ''), vars);
}

// A text with {name} and the like filled in from vars (one vars lacks stays as it is).
export function fill(text, vars) {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (match, name) => (Object.hasOwn(vars, name) ? String(vars[name]) : match));
}
